'use client'

import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import useScrollable from '@/src/hooks/useScrollable'

type Section = { label: string; id: string }

// Gap left between the sticky nav and a section it scrolls to. The same value
// drives the scrollspy line so the target section is highlighted immediately.
const SECTION_GAP = 20

type Props = {
  hasQuestions: boolean
  hasReviews: boolean
}

const Nav = ({ hasQuestions, hasReviews }: Props) => {
  const sections: Section[] = [
    { label: 'Steckbrief', id: 'profile' },
    { label: 'Standorte', id: 'locations' },
    ...(hasQuestions ? [{ label: 'Fragen', id: 'questions' }] : []),
    ...(hasReviews ? [{ label: 'Bewertungen', id: 'reviews' }] : []),
    { label: 'Preise', id: 'prices' },
  ]

  const ref = useRef<HTMLElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const { scrollableLeft, scrollLeft, scrollableRight, scrollRight } =
    useScrollable(ref)
  const [activeId, setActiveId] = useState<string>('profile')
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const obs = new IntersectionObserver(([entry]) =>
      setStuck(!entry.isIntersecting)
    )
    obs.observe(sentinel)
    return () => obs.disconnect()
  }, [])

  useEffect(() => {
    const updateActive = () => {
      // Line just below the sticky nav; the active section is the last one
      // whose top has scrolled above it. Position-based so it doesn't depend
      // on how deeply a section is nested or on its height.
      const line =
        (ref.current?.getBoundingClientRect().bottom ?? 0) + SECTION_GAP

      let current = sections[0]?.id
      for (const { id } of sections) {
        const el = document.getElementById(id)
        // +1 tolerance so a section scrolled exactly to the line still counts.
        if (el && el.getBoundingClientRect().top <= line + 1) {
          current = id
        }
      }
      if (current) setActiveId(current)
    }

    updateActive()
    window.addEventListener('scroll', updateActive, { passive: true })
    window.addEventListener('resize', updateActive)
    return () => {
      window.removeEventListener('scroll', updateActive)
      window.removeEventListener('resize', updateActive)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasQuestions, hasReviews])

  return (
    <>
      <div ref={sentinelRef} className="h-px -translate-15" />
      <div
        className={clsx(
          'sticky top-15 z-50 border-b border-gray-100 bg-white transition-shadow xl:top-17.5',
          { 'shadow-md': stuck }
        )}
      >
        <nav
          ref={ref}
          className="relative flex items-center overflow-x-auto overflow-y-hidden pt-4 lg:mx-auto lg:max-w-container lg:overflow-x-visible lg:pt-6"
        >
          {scrollableLeft && (
            <div className="sticky inset-y-0 left-0 z-10 flex items-center bg-linear-to-r from-white from-60% to-transparent pr-6 lg:hidden">
              <button
                onClick={scrollLeft}
                className="p-2 text-blue-700"
                aria-label="Nach links scrollen"
              >
                <CaretLeftIcon size={20} weight="bold" />
              </button>
            </div>
          )}
          <div
            className={clsx('flex h-9 gap-6', {
              '-ml-15 lg:ml-0': scrollableLeft,
              '-mr-15 lg:mr-0': scrollableRight,
            })}
          >
            {sections.map(({ label, id }) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={(e) => {
                  e.preventDefault()
                  const el = document.getElementById(id)
                  if (!el || !ref.current) return
                  // Land the section top at the same line the scrollspy uses,
                  // so it's highlighted the moment the scroll settles.
                  const line =
                    ref.current.getBoundingClientRect().bottom + SECTION_GAP
                  const top =
                    el.getBoundingClientRect().top + window.scrollY - line
                  window.scrollTo({ top, behavior: 'smooth' })
                }}
                className="flex shrink-0 flex-col justify-center gap-1 first:ml-5 last:mr-5 lg:first:ml-6 lg:last:mr-6"
              >
                <span className="pt-1 font-bold lg:pt-0">{label}</span>
                <div
                  className={clsx('h-0.5 w-full', {
                    'bg-blue-700': activeId === id,
                  })}
                />
              </a>
            ))}
          </div>
          {scrollableRight && (
            <div className="sticky inset-y-0 right-0 z-10 flex items-center bg-linear-to-l from-white from-60% to-transparent pl-6 lg:hidden">
              <button
                onClick={scrollRight}
                className="p-2 text-blue-700"
                aria-label="Nach rechts scrollen"
              >
                <CaretRightIcon size={20} weight="bold" />
              </button>
            </div>
          )}
        </nav>
      </div>
    </>
  )
}

export default Nav
